import React, { PropsWithChildren, useEffect, useRef } from "react";
import { useOverlayTriggerState } from "react-stately";
import { useParams } from "react-router-dom";
import { CreateURLParams } from "../../const";
import PlaylistBottomSheet from "../../../Components/PlaylistBottomSheet/PlaylistBottomSheet";
import { TreeNode } from "../../../../../../components/TreeCheckboxGroup";
import {
    getActiveItemBottomSheetTitle,
    getHierarchyPathFromURL,
    hierarchyPathToIndexes,
} from "../utils";
import { IntlShape, useIntl } from "react-intl";
import { EditorMainContent } from "../../Components/EditorLayout/EditorLayout";
import { useFeatureFlags } from "@evidenceb/athena-common/modules/FeatureFlags";
import { useBreakpoints } from "@evidenceb/athena-common/helpers/breakpoints";
import { LearningSetTypes } from "../../../../../../interfaces/AthenaResources";
import { Data } from "../../../../../../interfaces/Data";
import { useData } from "../../../../../../hooks/useData";
import { Tuto } from "../../../../Tuto/type";
import { useTutos } from "../../../../Tuto/useTutos";
import { useWorkshops } from "../../../../Workshop/useWorkshops";
import { Workshop } from "../../../../Workshop/types";
import { lightTheme } from "@evidenceb/athena-common/modules/Theme";

type Props = PropsWithChildren<{
    resources?: Record<string, TreeNode>;
}>;

const ActiveResourcePreview = ({ resources, children }: Props) => {
    const { isTabletOrSmaller } = useBreakpoints();
    const urlParams = useParams<CreateURLParams>();
    const intl = useIntl();
    const data = useData();
    const flags = useFeatureFlags();
    const { data: tutos } = useTutos({ enabled: flags.tutosInPlaylist });
    const { data: workshops } = useWorkshops({
        enabled: flags.workshopsInPlaylist,
    });

    const resourceModalState = useOverlayTriggerState({});
    const urlParamsRef = useRef<CreateURLParams>(urlParams);
    useEffect(() => {
        if (
            !isTabletOrSmaller ||
            urlParamsRef.current === urlParams ||
            !hasAvailablePreview(urlParams)
        )
            return;
        resourceModalState.open();
        urlParamsRef.current = urlParams;
    }, [isTabletOrSmaller, urlParams, resourceModalState]);

    if (!isTabletOrSmaller)
        return (
            <EditorMainContent style={{ color: lightTheme.textMainDefault }}>
                {children}
            </EditorMainContent>
        );

    return (
        <PlaylistBottomSheet
            state={resourceModalState}
            dialogTitle={
                resources
                    ? getBottomSheetTitle(intl)(urlParams, resources, {
                          [LearningSetTypes.Module]: data,
                          [LearningSetTypes.Tutorial]: tutos,
                          [LearningSetTypes.Workshop]: workshops,
                      })
                    : ""
            }
        >
            <div style={{ color: lightTheme.textMainDefault }}>{children}</div>
        </PlaylistBottomSheet>
    );
};
export default ActiveResourcePreview;

const hasAvailablePreview = (params: CreateURLParams): boolean => {
    switch (params.resourceType) {
        case LearningSetTypes.Module:
            return !!params.resourceId2;
        case LearningSetTypes.Tutorial:
        case LearningSetTypes.Workshop:
            return !!params.resourceId1;
        case undefined:
            return false;
        default: {
            const _: never = params.resourceType;
            throw new Error("resourceType not handled");
        }
    }
};

const getBottomSheetTitle =
    (intl: IntlShape) =>
    (
        urlParams: CreateURLParams,
        resources: Record<"string", TreeNode>,
        itemsBank: {
            [LearningSetTypes.Module]: Data;
            [LearningSetTypes.Tutorial]: Tuto[];
            [LearningSetTypes.Workshop]: Workshop[];
        }
    ) => {
        if (urlParams.resourceType !== LearningSetTypes.Module)
            return getResourceTitle(
                urlParams,
                urlParams.resourceType === LearningSetTypes.Tutorial
                    ? itemsBank[LearningSetTypes.Tutorial]
                    : itemsBank[LearningSetTypes.Workshop]
            );
        return getDataResourceTitle(intl)(
            urlParams,
            resources,
            itemsBank[LearningSetTypes.Module]
        );
    };

const getDataResourceTitle =
    (intl: IntlShape) =>
    (
        urlParams: CreateURLParams,
        resources: Record<"string", TreeNode>,
        data: Data
    ) => {
        const activeResource = getHierarchyPathFromURL(urlParams)
            .split("/")
            .slice(1)
            .join("/");
        if (!activeResource) return;
        return getActiveItemBottomSheetTitle(
            activeResource,
            hierarchyPathToIndexes(
                activeResource,
                resources,
                data.modules.map(({ id }) => id)
            ),
            data,
            intl
        );
    };

const getResourceTitle = (
    urlParams: CreateURLParams,
    data: (Tuto | Workshop)[]
) => {
    const setId = urlParams.resourceId1;
    const set = data.find((set) => set.id === setId);
    return set?.name ?? "";
};
